import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { Post } from "@/src/api";
import Card from "@/src/components/card/Card";
import LoadingIndicator from "@/src/components/loading";
import useSearchParams from "@/src/hooks/useSearchParams";
import React, { useCallback, useEffect, useState } from "react";
import { BsAndroid2 } from "react-icons/bs";
import { FaApple } from "react-icons/fa";
import { GrOverview } from "react-icons/gr";
import { useHistory } from "react-router-dom";

interface PayoutUser {
  userId: number;
  platformId: number;
}

function PayoutCheck() {
  const toast = useToast();
  const history = useHistory();
  const query = useSearchParams();
  const paramMail = query.get("email");
  const paramAdvertiserId = query.get("advertiserId");

  const [isFirstTime, setIsFirstTime] = useState(true);
  const [loading, setLoading] = useState({
    loading: false,
    message: "",
  });
  const [payoutEmail, setPayoutEmail] = useState(paramMail ?? "");
  const [payouAdvertiserId, setPayoutAdvertiserId] = useState(
    paramAdvertiserId ?? "",
  );
  const [payoutUsers, setPayoutUsers] = useState<PayoutUser[]>([]);

  const fetchPayoutUsersByEmail = useCallback(
    async (_payoutEmail: string) => {
      try {
        setLoading({
          loading: true,
          message: "Fetching Payouts",
        });
        const response = (await Post("/support/user/payout-mail", {
          payoutMail: _payoutEmail,
        })) as Array<PayoutUser> | null;

        if (response.length > 0) {
          setPayoutUsers(response);
        } else {
          setPayoutUsers([]);
          toast({
            title: "No Payouts Found",
            description: "No payouts found for the given email",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
        }
        setPayoutAdvertiserId("");
        history.push(`/user-check?email=${_payoutEmail}`);
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description: "Error fetching payouts",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading({
          loading: false,
          message: "",
        });
      }
    },
    [toast, history],
  );

  const fetchPayoutUsersByAdvertiserId = useCallback(
    async (_advertiserId: string) => {
      try {
        setLoading({
          loading: true,
          message: "Fetching Payouts",
        });
        const response = (await Post("/support/user/advertiser-id", {
          advertiserId: _advertiserId,
        })) as Array<PayoutUser> | null;

        if (response.length > 0) {
          setPayoutUsers(response);
        } else {
          setPayoutUsers([]);
          toast({
            title: "No Payouts Found",
            description: "No payouts found for the given advertiser ID",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
        }
        setPayoutEmail("");
        history.push(`/user-check?advertiserId=${_advertiserId}`);
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description: "Error fetching payouts",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading({
          loading: false,
          message: "",
        });
      }
    },
    [toast, history],
  );

  useEffect(() => {
    if (paramMail && isFirstTime) {
      fetchPayoutUsersByEmail(paramMail);
      setIsFirstTime(false);
      return;
    }

    if (paramAdvertiserId && isFirstTime) {
      fetchPayoutUsersByAdvertiserId(paramAdvertiserId);
      setIsFirstTime(false);
      return;
    }
  }, [
    paramAdvertiserId,
    paramMail,
    fetchPayoutUsersByAdvertiserId,
    fetchPayoutUsersByEmail,
    isFirstTime,
  ]);

  return (
    <Box>
      {loading.loading && <LoadingIndicator message={loading.message} />}
      <Card
        flexDir={"column"}
        flex={1}
        p={8}
        overflowX={{ sm: "scroll", lg: "scroll" }}
        gap={16}
      >
        <Flex gap={8} justifyContent={"flex-start"}>
          <Flex gap={2}>
            <Input
              borderRadius={8}
              value={payoutEmail}
              onChange={(e) => setPayoutEmail(e.target.value)}
              placeholder={"Enter Email"}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchPayoutUsersByEmail(payoutEmail);
                }
              }}
            />
            <Button
              backgroundColor={"brand.500"}
              color={"white"}
              _hover={{ backgroundColor: "brand.600" }}
              borderRadius={8}
              onClick={() => fetchPayoutUsersByEmail(payoutEmail)}
              px={16}
            >
              Get Payouts by Email
            </Button>
          </Flex>
          <Flex gap={2}>
            <Input
              borderRadius={8}
              value={payouAdvertiserId}
              onChange={(e) => setPayoutAdvertiserId(e.target.value)}
              placeholder={"Enter Advertiser ID"}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchPayoutUsersByAdvertiserId(payouAdvertiserId);
                }
              }}
            />
            <Button
              backgroundColor={"brand.500"}
              color={"white"}
              _hover={{ backgroundColor: "brand.600" }}
              borderRadius={8}
              onClick={() => fetchPayoutUsersByAdvertiserId(payouAdvertiserId)}
              px={16}
            >
              Get Payouts by Advertiser ID
            </Button>
          </Flex>
        </Flex>
        {payoutUsers.length > 0 ? (
          <Table variant={"striped"} overflow="scroll">
            <Thead>
              <Tr>
                <Th>
                  <Text>User ID</Text>
                </Th>
                <Th>
                  <Text>Platform</Text>
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {payoutUsers.map((user: PayoutUser, index: number) => (
                <Tr key={index}>
                  <Td>
                    <Flex alignItems="center" gap={2}>
                      <Text minW={8} ml={4}>
                        {user.userId}
                      </Text>
                    </Flex>
                  </Td>
                  <Td alignItems="center" justifyContent={"center"}>
                    <Tooltip
                      label={user.platformId === 1 ? "Android" : "iOS"}
                      aria-label={user.platformId === 1 ? "Android" : "iOS"}
                    >
                      <Flex alignItems="center" ml={4}>
                        {user.platformId === 1 ? <BsAndroid2 /> : <FaApple />}
                      </Flex>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Flex justifyContent="flex-end">
                      <Tooltip
                        label={"Go to User Profile"}
                        aria-label={"Go to User Profile"}
                      >
                        <IconButton
                          aria-label={"Go to User Profile"}
                          icon={<GrOverview />}
                          onClick={() => {
                            history.push(`/users?userId=${user.userId}`);
                          }}
                          backgroundColor={"brand.500"}
                          color={"white"}
                          _hover={{ backgroundColor: "brand.600" }}
                          borderRadius={8}
                        />
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : null}
      </Card>
    </Box>
  );
}

export default PayoutCheck;
